import type { KeyboardEvent } from "react";

export interface ChipOption {
  /** Stable, ASCII, and independent of language — it also becomes the DOM id. */
  value: string;
  label: string;
}

/**
 * Options carry a `value` separate from their `label` because the label is
 * translated: keying the DOM id (and the caller's state) off the visible
 * text would change both every time the language does, and would put
 * spaces and non-ASCII characters into an element id.
 */
export function ChipGroup({
  options,
  value,
  onChange,
  label,
}: {
  options: ChipOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const dir = event.key === "ArrowRight" ? 1 : -1;
    const next = options[(index + dir + options.length) % options.length];
    onChange(next.value);
    document.getElementById(`chip-${next.value}`)?.focus();
  }

  return (
    <div className="chip-group" role="radiogroup" aria-label={label}>
      {options.map((option, index) => (
        <button
          key={option.value}
          id={`chip-${option.value}`}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          tabIndex={value === option.value ? 0 : -1}
          className="chip"
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
